import { useCallback, useMemo } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { useAudienceContext, useCompareContext, useDialogs } from "@/context";
import { Audience, PageState } from "@/types";
import {
  buildAudience,
  buildUpload,
  createNotification,
  cloneAudience as cloneAudienceAPI,
  deleteAudience,
  deleteShare,
  getCatchment,
  getCatchmentSize,
  getInsight,
  requestTravelTime,
  saveAudience,
  shareAudience as shareAudienceAPI,
  updateAudienceName,
  updateAudienceStatus, upload, validateFile,
} from "@/api";
import { useActionWorkflow, useClient, useServices } from "@/hooks";
import { checkAudienceSize, extractSegments, filterCatchment, modeThreshold } from "@/utils";


interface UseAudienceActionsProps {
  navigate: NavigateFunction;
  pageState: PageState;
}

// TODO - need to consolidate these hooks
// TODO - need to account for upload audiences

export const useAudienceUtils = (
  audienceId?: string,
  audience?: Audience
) => {
  const { auth, nav, ui } = useServices();
  const { getAccessTokenSilently } = auth;
  const { dispatch } = useAudienceContext();
  const { action, runAction } = useActionWorkflow();
  const client = useClient();


  const handleBuild = useCallback(async () => {

    if (!audience || audience.audience_type !== 'build') return;

    await runAction('build', 'Building Audience...',
      async (taskMessage) => {

        const token = await auth.getAccessTokenSilently();

        // Prepare Audience parameters
        const queries = audience.var_groups
          .filter((group) => group.variables.length > 0)
          .map((group) => {
            const ids = group.variables.map((v) => v.tag_id).join(",");
            return `${group.operator}(${ids})`;
          });
        const geoFence = audience.geo_fence?.map((x) => x.geo_id).join(",");

        // Update the audience state to be the latest audience
        dispatch({ type: "GET_AUDIENCE", payload: { audience } });

        // Get the audience size from the API
        const { data, error } = await buildAudience(
          token,
          queries,
          modeThreshold[audience.build_mode],
          audience.operator,
          geoFence,
          audienceId
        );

        if (error) throw new Error(error.message);

        if (checkAudienceSize(data.audience_size[0])) {
          dispatch({ type: "BUILD_AUDIENCE", payload: data });
        } else {
          throw new Error("Unable to build audience or the audience size was 0, please adjust your criteria and try again")
        }
      }
    )
  }, [audience, audienceId, auth, dispatch, runAction]);

  const handleBuildUpload = useCallback(async () => {

    if (
      !audience
      || audience.audience_type !== 'upload'
      || !audience.upload_mode
      || !audience.upload_file
      || !(audience.upload_file instanceof File)
    ) return;

    const { upload_file, upload_mode } = audience; // Need this to shut typescript up

    await runAction('build', 'Building Upload...',
      async (taskMessage) => {

        const token = await auth.getAccessTokenSilently();

        // Update the audience state to be the latest audience
        dispatch({ type: "GET_AUDIENCE", payload: { audience } });

        // Get the audience size from the API
        const { data, error } = await buildUpload(
          token,
          upload_file,
          upload_mode,
          audienceId,
          audience.travel_time
        );

        if (error) throw new Error(error.message);

        if (data.audience_size?.some(size => checkAudienceSize(size))) {
          dispatch({ type: "BUILD_AUDIENCE", payload: data });

          if (data.audience_size?.every(size => checkAudienceSize(size))) {
            if (data.audience_size.length > 1) {
              const audienceSegments = extractSegments(data.audience_size);
              dispatch({ type: "ADD_SEGMENTS", payload: audienceSegments });
            }

          } else {
            throw new Error("One or more of your segments has no valid geos, please check the file contents and try again")
          }
        } else {
          throw new Error("No valid geos were found in your file, please check the file contents and try again")
        }
      }
    )
  }, [audience, audienceId, auth, dispatch, runAction]);

  const handleBuildSite = useCallback(async (sectors: string[]) => {

    if (!audience || audience.audience_type !== 'site') return;

    await runAction('build', 'Building Site Audience...',
      async (taskMessage) => {

        const token = await getAccessTokenSilently();

        // Get the audience size from the API
        const { data, error } = await getCatchmentSize(
          token,
          sectors,
          audienceId,
        );

        if (error) throw new Error(error.message);

        if (checkAudienceSize(data.audience_size[0])) {
          dispatch({ type: "BUILD_AUDIENCE", payload: data });
        } else {
          throw new Error("Unable to build audience or the audience size was 0, please adjust your criteria and try again")
        }
      }
    )

  }, [audience, getAccessTokenSilently, dispatch, runAction])

  const handleSiteCatchment = useCallback(async (site_ids: number[], categories: string[], dayType: string) => {

    if (!audience || audience.audience_type !== 'site') return;

    if (site_ids.length === 0 || categories.length === 0) {
      dispatch({ type: 'SET_SITE_CATCHMENT', payload: [] });
      dispatch({ type: 'BUILD_AUDIENCE', payload: { audience_geo: 'sector', audience_size: undefined } });
      return;
    }

    await runAction('catchment', 'Loading Site Catchment...',
      async (taskMessage) => {

        const token = await getAccessTokenSilently();

        // Get the audience size from the API
        const { data: catchment, error } = await getCatchment(
          token,
          site_ids,
          client
        );

        if (error) throw new Error(error.message);

        if (catchment) {
          dispatch({ type: 'SET_SITE_CATCHMENT', payload: catchment });

          // Filter the catchment to the selected categories
          const filteredCatchment = filterCatchment(catchment, categories, dayType);

          if (filteredCatchment.length > 0) {

            await handleBuildSite(filteredCatchment.map(x => x.sector))

          } else {
            dispatch({ type: 'BUILD_AUDIENCE', payload: { audience_geo: 'sector', audience_size: undefined } });
          }
        }
      }
    )

  }, [getAccessTokenSilently, dispatch, runAction, audience, handleBuildSite, client])

  const handleCreate = useCallback(async (
    audienceName: string,
    projectName: string | null
  ) => {
    if (!audience) return;

    await runAction('insight', 'Saving Audience...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();
        ui.closeDialog();

        // Prepare the audience data to save
        let newAudienceName = audienceName ?? audience.audience_name;
        let newProjectName = projectName ?? audience.project_name;
        let updatedAudience = {
          ...audience,
          audience_name: newAudienceName,
          project_name: newProjectName,
        };

        // Save audience
        const saveResponse = await saveAudience(token, updatedAudience, true);
        if (saveResponse.error) throw new Error(saveResponse.error.message);

        // Update Audience context
        const newAudience = saveResponse.data.filter(
          (audience) => audience.audience_name === updatedAudience.audience_name
        )[0];
        dispatch({
          type: "UPDATE_AUDIENCE",
          payload: { audiences: saveResponse.data, currentAudience: newAudience },
        });

        // Calculate insight
        taskMessage("Calculating Insight...");
        const insightResponse = await getInsight(token, newAudience.audience_id, true, newAudience.client);
        if (insightResponse.error) throw new Error(insightResponse.error.message);

        // Save the insight to the context
        dispatch({ type: "CREATE_INSIGHT", payload: { ...insightResponse.data } });

        // Create Notification
        const notification = {
          client: newAudience.client,
          message: `Audience "${newAudience.audience_name}" successfully created`,
          category: "Audience",
          data: { audience_id: newAudience.audience_id}
        }
        const notifyResponse = await createNotification(token, notification)
        if (notifyResponse.error) throw new Error(notifyResponse.error.message);

        // Update audience status
        const updateResponse = await updateAudienceStatus(
          token,
          newAudience.audience_id,
          'complete'
        );
        if (updateResponse.error) throw new Error(updateResponse.error.message);

        // Navigate to the insights page
        nav.navigate(`../${newAudience.audience_id}/insight`, {
          relative: "path",
        })
      }
    )
  }, [audience, auth, dispatch, nav, runAction, ui]);


  const handleCreateSite = useCallback(async (
    audienceName: string,
    projectName: string | null
  ) => {
    if (!audience) return;

    await runAction('insight', 'Saving Site Audience...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();
        ui.closeDialog();

        // Prepare the audience data to save
        let newAudienceName = audienceName ?? audience.audience_name;
        let newProjectName = projectName ?? audience.project_name;
        let updatedAudience = {
          ...audience,
          audience_name: newAudienceName,
          project_name: newProjectName,
        };

        // Save audience
        const saveResponse = await saveAudience(token, updatedAudience, true);
        if (saveResponse.error) throw new Error(saveResponse.error.message);

        // Update Audience context
        const newAudience = saveResponse.data.filter(
          (audience) => audience.audience_name === updatedAudience.audience_name
        )[0];
        dispatch({
          type: "UPDATE_AUDIENCE",
          payload: { audiences: saveResponse.data, currentAudience: newAudience },
        });

        // Calculate insight
        taskMessage("Calculating Insight...");
        const insightResponse = await getInsight(token, newAudience.audience_id, true, newAudience.client);
        if (insightResponse.error) throw new Error(insightResponse.error.message);

        // Save the insight to the context
        dispatch({ type: "CREATE_INSIGHT", payload: { ...insightResponse.data } });

        // Create Notification
        const notification = {
          client: newAudience.client,
          message: `Site Audience "${newAudience.audience_name}" successfully created`,
          category: "Audience",
          data: { audience_id: newAudience.audience_id}
        }
        const notifyResponse = await createNotification(token, notification)
        if (notifyResponse.error) throw new Error(notifyResponse.error.message);

        // Update audience status
        const updateResponse = await updateAudienceStatus(
          token,
          newAudience.audience_id,
          'complete'
        );
        if (updateResponse.error) throw new Error(updateResponse.error.message);

        // Navigate to the insights page
        nav.navigate(`../${newAudience.audience_id}/insight`, {
          relative: "path",
        })
      }
    )
  }, [audience, auth, dispatch, nav, runAction, ui]);


  const handleUpload = useCallback(async (
    audienceName: string,
    projectName: string | null
  ) => {
    if (
      !audience
      || audience.audience_type !== 'upload'
      || !audience.upload_mode
      || !audience.upload_file
      || !(audience.upload_file instanceof File)
    ) return;

    const { upload_file, upload_mode } = audience; // Need this to shut typescript up

    await runAction('insight', 'Uploading Data...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();
        ui.closeDialog();

        // 1. Upload the file
        const uploadResponse = await upload(
          token,
          upload_file,
          upload_mode,
          audienceId,
          audience.travel_time
        );
        if (uploadResponse.error) throw new Error(uploadResponse.error.message);

        // Prepare the audience data to save
        let newAudienceName = audienceName ?? audience.audience_name;
        let newProjectName = projectName ?? audience.project_name;
        let newUploadFile = { name: upload_file.name };
        let updatedAudience = {
          ...audience,
          audience_id: uploadResponse.data.audience_id,
          audience_name: newAudienceName,
          project_name: newProjectName,
          upload_file: newUploadFile,
          status: "processing",
        };

        // 2. Save Audience
        const saveResponse = await saveAudience(token, updatedAudience, true);
        if (saveResponse.error) throw new Error(saveResponse.error.message);

        // Update the audience context
        const newAudience = saveResponse.data.filter(
          (audience) => audience.audience_name === updatedAudience.audience_name
        )[0];
        dispatch({
          type: "UPDATE_AUDIENCE",
          payload: { audiences: saveResponse.data, currentAudience: newAudience },
        });

        if (audience.upload_mode === 'travel_time' && audience.travel_time) {

          // Create traveltime payload
          const payload = {
            ...audience.travel_time,
            audience_id: newAudience.audience_id,
            audience_name: newAudience.audience_name,
            client
          }

          // 3a. Request Traveltime Catchment
          const traveltimeResponse = await requestTravelTime(token, payload);
          if (traveltimeResponse.error) {

            // 4. Delete Audience - Otherwise we end up in a limbo state // TODO
            await deleteAudience(
              token,
              newAudience.audience_name,
              newAudience.audience_id,
              newAudience.audience_type,
              client
            )

            throw new Error(traveltimeResponse.error.message);
          }

          // Go to the audience page
          nav.navigate(`../${traveltimeResponse.data.audience_id}`, {
            relative: "path",
          });

        } else {

          // 3b. Calculate Insight
          taskMessage("Calculating Insight...");
          const insightResponse = await getInsight(token, newAudience.audience_id, true, newAudience.client);
          if (insightResponse.error) throw new Error(insightResponse.error.message);

          // Save the insight to the context
          dispatch({ type: "CREATE_INSIGHT", payload: { ...insightResponse.data } });

          // 4. Create Notification
          const notification = {
            client: newAudience.client,
            message: `Upload "${newAudience.audience_name}" successfully created`,
            category: "Upload",
            data: { audience_id: newAudience.audience_id}
          }
          const notifyResponse = await createNotification(token, notification)
          if (notifyResponse.error) throw new Error(notifyResponse.error.message);

          // Update audience status
          const updateResponse = await updateAudienceStatus(
            token,
            newAudience.audience_id,
            'complete'
          );
          if (updateResponse.error) throw new Error(updateResponse.error.message);

          // Navigate to the insights page
          nav.navigate(`../${newAudience.audience_id}/insight`, {
            relative: "path",
          })
        }
      }
    )

    }, [audience, audienceId, auth, dispatch, nav, runAction, ui, client])


  const handleDelete = useCallback(async (redirect?: string) => {
    if (!audience) return;

    await runAction('delete', 'Deleting Audience...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();
        const client = process.env.REACT_APP_CLIENT || 'core';

        const { data, error } = await deleteAudience(
          token,
          audience.audience_name,
          audience.audience_id,
          audience.audience_type,
          client,
        );

        if (error) {
          ui.openSnackbar(error.message, "error");
        } else {
          dispatch({ type: "DELETE_AUDIENCE", payload: data });
          ui.openSnackbar("Audience deleted successfully", "success");
          ui.closeDialog();
          redirect && nav.navigate(redirect);
        }
      })

  }, [audience, auth, dispatch, nav, runAction, ui])

  const handleExport = useCallback(() => {
    nav.navigate("export");
  }, [nav]);

  const handleInsight = useCallback((hasChanged: boolean) => {
    if (!audience) return;

    // Does the audience exist?
    if (audience.audience_id && audience.audience_id !== "") {
      // Have there been any changes?
      if (hasChanged) {
        // Yes - check the user is happy to update the audience
        ui.openDialog({
          type: "confirm",
          props: {
            message: `\nProceeding will mean that your saved ${audience.audience_type} audience will get overwritten.\nCreate a new audience if you don't want this to happen.\n`,
            onConfirm: async () => {
              await handleCreate(
                audience.audience_name,
                audience.project_name
              );
            },
          },
        });
      } else {
        // No - navigate to insights
        nav.navigate("insight");
      }
    } else {
      // New audience - show save audience dialog, then process it
      ui.openDialog({
        type: "createAudience",
        props: {
          audienceType: audience.audience_type,
          onConfirm: audience.audience_type === 'build'
            ? handleCreate
            : audience.audience_type === 'upload'
              ? handleUpload
              : handleCreateSite,
        },
      });
    }
  }, [audience, handleCreate, handleUpload, handleCreateSite, nav, ui]);

  const handleShare = useCallback(async (ids: number[]) => {

    if (!audience) return;

    await runAction('share', 'Sharing Audience...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();

        const { data, error } = await shareAudienceAPI(
          token,
          audience.audience_id,
          !!audience?.shared,
          ids
        );
        ui.closeDialog();

        if (error) {
          ui.openSnackbar(error.message, "error");
        } else {
          dispatch({
            type: "SHARE_AUDIENCE",
            payload: { audienceId: audience.audience_id, userIds: ids },
          })
          ui.openSnackbar("Audience shared successfully", "success");
        }
      })

  }, [audience, auth, dispatch, runAction, ui]);

  const handleClone = useCallback(async () => {

    if (!audience) return;

    await runAction('clone', 'Cloning Audience...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();

        const { data, error } = await cloneAudienceAPI(
          token,
          audience.audience_id,
          client
        );
        ui.closeDialog();

        if (error) {
          ui.openSnackbar(error.message, "error");
        } else {
          dispatch({
            type: "GET_AUDIENCE_LIST",
            payload: data,
          })
          ui.openSnackbar("Audience cloned successfully", "success");
        }
      })

  }, [audience, auth, dispatch, runAction, ui, client]);

  const handleDeleteShare = useCallback(async (redirect?: string) => {
    if (!audience) return;

    await runAction('delete', 'Removing Shared Audience...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();

        const { data, error } = await deleteShare(
          token,
          audience.audience_id,
        );

        if (error) {
          ui.openSnackbar(error.message, "error");
        } else {
          ui.openSnackbar("Shared Audience removed successfully", "success");
          ui.closeDialog();
          redirect && nav.navigate(redirect);
        }
      })

  }, [audience, auth, nav, runAction, ui])

  const handleUpdate = useCallback(async (
    audienceId: string,
    audienceName: string,
    projectName: string | null
  ) => {

    await runAction('share', 'Sharing Audience...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();

        const { error } = await updateAudienceName(
          token,
          audienceId,
          audienceName,
          projectName
        );
        ui.closeDialog();

        if (error) {
          ui.openSnackbar(error.message, "error");
        } else {
          dispatch({
            type: "CHANGE_NAME",
            payload: { audienceId, audienceName, projectName },
          });
          ui.openSnackbar("Audience name updated successfully", "success");
        }
      })

  }, [auth, dispatch, runAction, ui]);


  const handleCheckFile = useCallback(async (file: File, mode: string) => {

    if (!audience) return;

    await runAction('check', 'Checking File...',
      async (taskMessage) => {
        const token = await auth.getAccessTokenSilently();

        const { data, error } = await validateFile(token, file, mode);

        if (error) {
          dispatch({
            type: "CHECK_UPLOAD_FILE",
            payload: { format: false, segment: false, error: true },
          })
        } else {
          dispatch({
            type: "CHECK_UPLOAD_FILE",
            payload: data,
          })
        }
      })

  }, [audience, auth, dispatch, runAction]);

  const utils = useMemo(() => ({
    handleBuild,
    handleBuildUpload,
    handleBuildSite,
    handleCheckFile,
    handleClone,
    handleCreate,
    handleDelete,
    handleDeleteShare,
    handleExport,
    handleInsight,
    handleSiteCatchment,
    handleShare,
    handleUpdate,
  }), [
    handleBuild,
    handleBuildUpload,
    handleBuildSite,
    handleSiteCatchment,
    handleCheckFile,
    handleClone,
    handleCreate,
    handleDelete,
    handleDeleteShare,
    handleExport,
    handleInsight,
    handleShare,
    handleUpdate,
  ]);

  return useMemo(() => ({
    utils,
    action
  }), [utils, action]);

}



export function useAudienceActions ({ navigate, pageState }: UseAudienceActionsProps) {
  const { getAccessTokenSilently } = useAuth0();
  const { dispatch } = useAudienceContext();
  const client = useClient();
  const { state: compareState, dispatch: compareDispatch } = useCompareContext();
  const { openDialog, closeDialog } = useDialogs();

  const handleShareAudience = async (
    audience: Audience,
    ids: number[]
  ) => {
    const accessToken = await getAccessTokenSilently();

    const { data, error } = await shareAudienceAPI(
      accessToken,
      audience.audience_id || "",
      !!audience?.shared,
      ids
    );

    if (data) {
      dispatch({
        type: "SHARE_AUDIENCE",
        payload: { audienceId: audience.audience_id, userIds: ids },
      });
      closeDialog();
    }

    if (error) {
      closeDialog();
      pageState.error.setError(error.message);
    }
  };

  const handleCloneAudience = async (
    audience: Audience,
  ) => {
    const accessToken = await getAccessTokenSilently();

    const { data, error } = await cloneAudienceAPI(
      accessToken,
      audience.audience_id || "",
      client
    );

    if (data) {
      dispatch({
        type: "GET_AUDIENCE_LIST",
        payload: data
      });
      closeDialog();
    }

    if (error) {
      closeDialog();
      pageState.error.setError(error.message);
    }
  };


  const handleEditAudience = async (
    audienceId: string,
    audienceName: string,
    projectName: string | null
  ) => {
    const token = await getAccessTokenSilently();

    const { data, error } = await updateAudienceName(
      token,
      audienceId,
      audienceName,
      projectName
    );

    if (data) {
      dispatch({
        type: "CHANGE_NAME",
        payload: {
          audienceId,
          audienceName,
          projectName,
        },
      });
      closeDialog();
    }

    if (error) {
      pageState.error.setError(error.message);
      closeDialog();
    }
  };

  const createAudience = useCallback(
    () => {
      dispatch({ type: "CREATE_AUDIENCE" });
      navigate("new");
    },
    [dispatch, navigate]
  );

  const compareAudience = useCallback(
    (audience: Audience) => {
      compareDispatch({ type: "ADD_TO_COMPARE", payload: { audience } })
      navigate("/compare", {state: { audience }});
    },
    [compareDispatch, navigate]
  )

  const selectAudience = useCallback(
    (audience: Audience) => {
      dispatch({ type: "GET_AUDIENCE", payload: { audience } });
      navigate(audience.audience_id);
    },
    [dispatch, navigate]
  );

  const viewInsights = useCallback(
    (audience: Audience) => {
      dispatch({ type: "GET_AUDIENCE", payload: { audience } });
      navigate(`${audience.audience_id}/insight`);
    },
    [dispatch, navigate]
  );

  const exportAudience = useCallback(
    (audience: Audience) => {
      dispatch({ type: "GET_AUDIENCE", payload: { audience } });
      navigate(`${audience.audience_id}/export`);
    },
    [dispatch, navigate]
  );

  const editAudience = useCallback(
    (audience: Audience) => {
      openDialog({
        type: "editAudience",
        props: {
          audienceId: audience.audience_id,
          audienceName: audience.audience_name,
          projectName: audience.project_name,
          onConfirm: handleEditAudience,
        },
      });
    },
    [openDialog]
  );

  const deleteAudience = useCallback(
    (audience: Audience) => {
      openDialog({
        type: "deleteAudience",
        props: {
          audience,
          redirect: audience.audience_type === 'build'
            ? "/audience"
            : `/${audience.audience_type}`,
        },
      });
    },
    [openDialog]
  );

  const shareAudience = useCallback(
    (audience: Audience) => {
      openDialog({
        type: "shareAudience",
        props: {
          selectedEntity: audience,
          onShare: async (ids: number[]) => {
            await handleShareAudience(audience, ids);
          },
        },
      });
    },
    [openDialog]
  );

  const cloneAudience = useCallback(
    (audience: Audience) => {
      openDialog({
        type: "cloneAudience",
        props: {
          audience,
          onClone: async () => {
            await handleCloneAudience(audience);
          }
        },
      });
    }, [openDialog]);

  return {
    createAudience,
    selectAudience,
    viewInsights,
    exportAudience,
    editAudience,
    deleteAudience,
    shareAudience,
    compareAudience,
    cloneAudience,
  }
}
